import type { IPercentAPRResponse, IPercentAPRResponseNamed, ITokenModel } from '~/types/apiService';
import { apiUrls, Tokens } from '~/utils/constants';
import { ZeroAddress } from 'ethers';
import useEnvs from '~/composables/useEnvs';
import { BigNumber } from 'bignumber.js';

export default (tokensData: Ref<ITokenModel[] | null, ITokenModel[] | null>, uniqueKey?: string) => {
  const { apiUrl } = useEnvs();

  const filteredTokenData = computed(() => {
    if (!tokensData.value) {
      return [];
    }

    const filtered = tokensData.value?.filter((token) => token.isSellable);

    return filtered;
  });

  const { data: allAprData, status: allAprStatus } = useAsyncData(
    `allApr-${uniqueKey || 'default'}`,
    async () => {
      if (!filteredTokenData.value?.length) {
        return [];
      }

      try {
        const promises = filteredTokenData.value?.map((token) => {
          return $fetch<IPercentAPRResponse>(`${apiUrl}${apiUrls.token.apr}`, {
            params: { tokenAddress: (token as ITokenModel)?.tokenContractAddress }
          });
        });

        const result = await Promise.all(promises || []);
        return result;
      } catch (err) {
        console.error('Error fetching APR data:', err);
        return [];
      }
    },
    {
      transform(data): IPercentAPRResponseNamed[] {
        if (!filteredTokenData.value?.length || !data) {
          return [];
        }

        return filteredTokenData.value.map((token, index) => {
          return {
            tokenContractAddress: (token as ITokenModel)?.tokenContractAddress || ZeroAddress,
            key: (token as ITokenModel)?.key || '',
            apr: data[index]?.apr || 0
          };
        });
      },
      watch: [filteredTokenData],
      server: false
    }
  );

  const findApr = (key: Tokens) => {
    if (!allAprData.value) {
      return 0;
    }

    return BigNumber(allAprData.value.find((item) => item.key === key)?.apr || 0)
      .decimalPlaces(1)
      .toNumber();
  };

  return { allAprData, allAprStatus, findApr };
};
